import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {UserState} from "../../api/models/user-state";
import {Subscription} from "rxjs";
import {US_Interactive} from "../../api/models/us_interactive";
import {US_ModifiedInteractive} from "../../api/models/us_modified-interactive";
import {US_Filter} from "../../api/models/us_filter";
import {US_Bookmark} from "../../api/models/us_bookmark";
import {InteractReports} from "../../api/models/interact-reports";
import {DropDownItem} from "../../api/models/drop-down-item";
import {ShareState} from "../share-editor/share-editor.component";
import {GlobalApp} from "../../tools/global";
import {InteractivesService} from "../../api/services/interactives.service";
import {SubscriptionService} from "../../api/services/subscription.service";
import {Router} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";
import {UserTypeValue} from "../../api/models-ex/user-type-values";
import {
  CopyInteractiveDialogComponent,
  CopyInteractiveDialogData
} from "../../dialogs/copy-interactive-dialog/copy-interactive-dialog.component";
import {UserStateEx} from "../../api/models-ex/user-state-ex";
import {DialogResultType} from "../../dialogs/DialogResult";

@Component({
  selector: "self-service-analytics-home",
  templateUrl: "./self-service-analytics-home.component.html",
})
export class SelfServiceAnalyticsHomeComponent implements OnInit, OnDestroy {

  @Input() showHeader: boolean = true;

  selectedValues: string[] = [];

  userState: UserState = {
    reportOnly: false,
    userManagerOnly: false,
    invoiceManagerOnly: false,
    interactives: [],
    modifiedInteractives: [],
    xmlas: [],
    reportExportState: {},
    isInteractiveUser: false,
    isReportExportUser: false,
    isEmbeddedUser: false
  };

  userStateSubscription: Subscription = null;

  currentSelectedInteractive: US_Interactive = null;
  currentSelectedModifiedInteractive: US_ModifiedInteractive = null;

  selectedInteractive = "-1";
  selectedFilter = "-1";
  selectedBookmark = "-1";
  selectedReport = "-1";
  selectedAction = "-1";

  availableInteractives: US_Interactive[] = [];
  availableModifiedInteractives: US_ModifiedInteractive[] = [];
  availableFilters: US_Filter[] = [];
  availableBookmarks: US_Bookmark[] = [];
  availableActions: Action[] = [];
  availableReports: InteractReports[] = [];

  interactiveDropDownItems: DropDownItem[];
  filterDropDownItems: DropDownItem[];
  bookmarkDropDownItems: DropDownItem[];
  actionDropDownItems: DropDownItem[];

  initialInteractiveName: string = "Loading Interactives...";

  public shareState: ShareState;

  isLoading: boolean = false;

  constructor(
    public app: GlobalApp,
    private interactiveService: InteractivesService,
    private subscriptionService: SubscriptionService,
    private router: Router,
    private dialog: MatDialog
  ) {
    this.interactiveDropDownItems = [];
    this.filterDropDownItems = [];
    this.bookmarkDropDownItems = [];
    this.actionDropDownItems = [];

    this.app.getAuthorizationStatus().subscribe((result) => {
      if (!result.isValid) {

        this.interactiveDropDownItems.push({
          display: "--- NO INTERACTIVES ---",
          value: "-1",
          properties: {
            Type: "None",
          },
        });
      } else {
        this.app.refreshUserState(this.subscriptionService);
      }
    });

    this.filterDropDownItems.push({
      display: "--- NO FILTER (DEFAULT) ---",
      value: "-1",
    });

    this.bookmarkDropDownItems.push({
      display: "--- NO BOOKMARK (DEFAULT) ---",
      value: "-1",
    });

    this.actionDropDownItems.push({
      display: "-- NO ACTION --",
      value: "-1",
    });

  }
  ngOnInit() {
    this.userStateSubscription = this.app.userState.subscribe((userState) => {
      let isFirst = false;
      if (this.userState.interactives.length === 0) {
        isFirst = true;
      }

      this.userState = userState;

      this.availableInteractives = [];
      for (let i = 0; i < this.userState.interactives.length; i++) {
        let isAvailable = false;
        for (
          let e = 0;
          e < this.userState.interactives[i].subscriptionHeaders.length;
          e++
        ) {
          if (
            this.userState.interactives[i].subscriptionHeaders[e].userType !==
            UserTypeValue.NoRole
          ) {
            isAvailable = true;
          }
        }

        if (!this.userState.interactives[i].isActive) {
          isAvailable = false;
        }

        if (isAvailable) {
          this.availableInteractives.push(this.userState.interactives[i]);
        }
      }

      this.availableModifiedInteractives = [];
      for (let i = 0; i < this.userState.modifiedInteractives.length; i++) {
        let isAvailable = false;
        for (
          let e = 0;
          e < this.userState.modifiedInteractives[i].subscriptionHeaders.length;
          e++
        ) {
          if (
            this.userState.modifiedInteractives[i].subscriptionHeaders[e]
              .userType !== UserTypeValue.NoRole
          ) {
            isAvailable = true;
          }
        }

        if (isAvailable) {
          this.availableModifiedInteractives.push(
            this.userState.modifiedInteractives[i]
          );
        }
      }

      const lastStatus = this.app.getLastStatus();
      this.isLoading = true;
      if (lastStatus.lastInteractiveId) {
        this.selectedInteractive = (lastStatus.lastInteractiveWasModified ? 'M-' : 'I-') + lastStatus.lastInteractiveId.toString();
      } else if (isFirst) {
        if (this.availableInteractives.length > 0) {
          this.selectedInteractive =
            "I-" + this.availableInteractives[0].interactId.toString();
        } else if (this.availableModifiedInteractives.length > 0) {
          this.selectedInteractive =
            "M-" +
            this.availableModifiedInteractives[0].modInteractId.toString();
        }

      }

      this.selectInteractive();

      this.isLoading = false;
    });
  }

  ngOnDestroy() {
    this.userStateSubscription.unsubscribe();
  }

  selectInteractive(): void {
    const interactiveParts = this.selectedInteractive.split("-");
    if (interactiveParts.length !== 2) {
      this.currentSelectedInteractive = null;
      this.currentSelectedModifiedInteractive = null;
    } else {
      const interactType = interactiveParts[0];
      const interactId = interactiveParts[1];
      this.currentSelectedModifiedInteractive = null;
      this.currentSelectedInteractive = null;
      this.availableFilters = [];
      this.availableBookmarks = [];
      this.availableActions = [];
      this.availableReports = [];

      if (interactType === "I") {
        for (let i = 0; i < this.userState.interactives.length; i++) {
          if (
            this.userState.interactives[i].interactId.toString() === interactId
          ) {
            this.currentSelectedInteractive = this.userState.interactives[i];

            if (!this.isLoading) {
              this.app.setLastInteractive(false, this.currentSelectedInteractive.interactId);
            }

            this.availableFilters = this.currentSelectedInteractive.filters;
            this.availableBookmarks = this.currentSelectedInteractive.bookmarks;
            this.availableReports = this.currentSelectedInteractive.availableReports;

            this.availableActions.push({
              display: "View",
              action: "view",
            });

            let canCopy = false;
            if (this.currentSelectedInteractive.canCopy) {
              for (
                let e = 0;
                e < this.currentSelectedInteractive.subscriptionHeaders.length;
                e++
              ) {
                if (
                  this.currentSelectedInteractive.subscriptionHeaders[e]
                    .userType == UserTypeValue.Designer
                ) {
                  canCopy = true;
                }
              }
            }

            if (canCopy) {
              this.availableActions.push({
                display: "Copy & Edit",
                action: "copyandedit",
              });
            }

            if (this.availableReports.length > 0) {
              this.selectedReport = this.availableReports[0].interactReportId.toString();
            }

            this.selectedAction = this.availableActions[0].action;
            this.initialInteractiveName = "-- NO INTERACTIVES --";
          }
        }
      } else if (interactType === "M") {
        for (let i = 0; i < this.userState.modifiedInteractives.length; i++) {
          if (
            this.userState.modifiedInteractives[i].modInteractId.toString() ==
            interactId
          ) {
            this.currentSelectedModifiedInteractive = this.userState.modifiedInteractives[
              i
              ];

            if (!this.isLoading) {
              this.app.setLastInteractive(true, this.currentSelectedModifiedInteractive.modInteractId);
            }

            this.availableFilters = this.currentSelectedModifiedInteractive.filters;
            this.availableBookmarks = this.currentSelectedModifiedInteractive.bookmarks;

            this.availableActions.push({
              display: "View",
              action: "view",
            });

            let allowEdit = this.currentSelectedModifiedInteractive.allowEdit;

            if (allowEdit) {
              this.availableActions.push({
                display: "Edit",
                action: "edit",
              });
            }

            this.selectedAction = this.availableActions[0].action;

            this.initialInteractiveName = "-- NO INTERACTIVE --";
          }
        }
      }
    }
  }

  interactiveCopyAndEdit(): boolean {
    const dialogData: CopyInteractiveDialogData = {
      dialogTitle: "Copy & Edit Interactive",
      name: "",
      shareState: UserStateEx.getModifiedInteractiveShareState(this.userState,  {
        allowEdit: true,
        shares: [],
        modInteractId: 0,
        subscriptionHeaders: [],
        interactive: undefined,
        bookmarks: [],
        interactId: 0,
        name: '',
        owner: true,
        createdBy: undefined,
        modifiedTimeStamp: undefined,
        powerBidatasetId: undefined,
        userId: 0,
        subscriptionId: 0,
        deleted: false
      }),
      isOwner: true,
      userSubscriptions: this.currentSelectedInteractive.subscriptionHeaders,
    };

    const dialogRef = this.dialog.open(CopyInteractiveDialogComponent, {
      width: "500px",
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (
        result != null &&
        result.ResultType != null &&
        result.ResultType == DialogResultType.Ok
      ) {
        const params: InteractivesService.InteractivesDuplicateInteractiveParams = {
          interactiveId: this.currentSelectedInteractive.interactId,
          newName: result.ResultData,
        };

        this.app.pushLoading("Creating Interactive");

        this.interactiveService
          .interactivesDuplicateInteractive(params)
          .subscribe(
            (result) => {
              const queryStringParams = {
                interactId: result.properties.ModifiedInteractiveId,
                interactType: "ModifiedInteractive",
                actionType: "edit",
                filterId: "-1",
                bookmarkId: "-1",
                reportId: "-1"
              };

              this.app.popLoading();
              this.router.navigate([
                "powerbi-embedded",
                queryStringParams.interactId,
                queryStringParams.interactType,
                queryStringParams.actionType,
                queryStringParams.filterId,
                queryStringParams.bookmarkId,
                queryStringParams.reportId
              ]);
              return false;
            },
            (error) => {
              var e = 0;
            }
          );
      }
    });

    return false;
  }

  goClick(): boolean {
    const interactiveParts = this.selectedInteractive.split("-");
    if (interactiveParts.length === 2) {
      const interactType = interactiveParts[0];
      const interactId = interactiveParts[1];

      if (this.selectedAction != "copyandedit") {
        const queryStringParams = {
          interactId,
          interactType:
            interactType === "M" ? "ModifiedInteractive" : "Interactive",
          actionType: this.selectedAction,
          filterId: this.selectedFilter,
          bookmarkId: this.selectedBookmark,
          reportId: this.userState.reportOnly ? this.selectedReport : "-1"
        };

        this.router.navigate([
          "powerbi-embedded",
          queryStringParams.interactId,
          queryStringParams.interactType,
          queryStringParams.actionType,
          queryStringParams.filterId,
          queryStringParams.bookmarkId,
          queryStringParams.reportId
        ]);
      } else {
        this.interactiveCopyAndEdit();
      }
    }

    return false;
  }
}

export interface Action {
  display: string;
  action: string;
}
